import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { History, RefreshCw, FileText, Search, Eye, Download } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeader = () => {
  const t = localStorage.getItem('token')
    || localStorage.getItem('access_token')
    || localStorage.getItem('admin_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const jval = (v) => {
  if (v == null) return '—';
  if (typeof v === 'object') { try { return JSON.stringify(v); } catch { return '—'; } }
  return String(v);
};

// Journal des analyses IA (DOS DOMIUM, admin) : date, auteur, pièces, complétude.
export default function DOMIUMIAJournal() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/domium-ia/journal`, { headers: getAuthHeader() });
      setEntries(Array.isArray(data.entries) ? data.entries : []);
    } catch {
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/domium-ia/journal/export-csv`, { headers: getAuthHeader(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `journal_ia_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { /* silencieux */ }
  };

  const filtered = entries.filter((e) => {
    const q = query.trim().toLowerCase();
    const matchQ = !q
      || (e.auteur || '').toLowerCase().includes(q)
      || (e.dossier || '').toLowerCase().includes(q)
      || (e.resume || '').toLowerCase().includes(q);
    const matchD = !dateFilter || (e.created_at || '').slice(0, 10) === dateFilter;
    return matchQ && matchD;
  });

  return (
    <Card className="mt-4" data-testid="domium-ia-journal">
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-base flex items-center gap-2 text-slate-700">
            <History className="h-4 w-4" /> Journal des analyses IA ({filtered.length})
          </CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <Input
                placeholder="Auteur, dossier, résumé…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-8 h-9 w-[200px]"
                data-testid="journal-search-input"
              />
            </div>
            <Input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="h-9 w-[150px]"
              data-testid="journal-date-filter"
            />
            <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="journal-refresh-btn">
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Actualiser
            </Button>
            <Button variant="outline" size="sm" onClick={exportCsv} data-testid="journal-export-btn">
              <Download className="h-4 w-4 mr-1" /> Export CSV
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {filtered.length === 0 ? (
          <p className="text-sm text-slate-500">Aucune analyse ne correspond.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 border-b">
                  <th className="py-2 pr-3">Date</th>
                  <th className="py-2 pr-3">Auteur</th>
                  <th className="py-2 pr-3">Dossier</th>
                  <th className="py-2 pr-3 text-center">Pièces</th>
                  <th className="py-2 pr-3 text-center">Présentes / Manquantes</th>
                  <th className="py-2 pr-3">Résumé</th>
                  <th className="py-2"></th>
                </tr>
              </thead>
              <tbody data-testid="journal-rows">
                {filtered.map((e) => (
                  <tr key={e.id} className="border-b border-slate-100 align-top hover:bg-slate-50">
                    <td className="py-2 pr-3 whitespace-nowrap">
                      {e.created_at ? new Date(e.created_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                    </td>
                    <td className="py-2 pr-3">{e.auteur || '—'}</td>
                    <td className="py-2 pr-3 font-medium text-slate-800">{e.dossier || <span className="text-slate-300">—</span>}</td>
                    <td className="py-2 pr-3 text-center">
                      <span className="inline-flex items-center gap-1"><FileText className="h-3 w-3 text-slate-400" />{e.nb_extraits ?? 0}/{e.nb_documents ?? 0}</span>
                    </td>
                    <td className="py-2 pr-3 text-center">
                      <span className="text-emerald-600">{e.nb_pieces_presentes ?? 0}</span>
                      {' / '}
                      <span className="text-amber-600">{e.nb_pieces_manquantes ?? 0}</span>
                    </td>
                    <td className="py-2 pr-3 text-slate-600 max-w-[280px] truncate" title={e.resume || ''}>{e.resume || '—'}</td>
                    <td className="py-2">
                      <Button variant="ghost" size="sm" onClick={() => setDetail(e)} data-testid="journal-view-btn">
                        <Eye className="h-4 w-4 mr-1" /> Voir
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="journal-detail-modal">
          <DialogHeader>
            <DialogTitle>Analyse IA — {detail?.dossier || 'Dossier'}</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-2 text-slate-600">
                <div><span className="text-slate-400">Date : </span>{detail.created_at ? new Date(detail.created_at).toLocaleString('fr-FR') : '—'}</div>
                <div><span className="text-slate-400">Auteur : </span>{detail.auteur || '—'}</div>
                <div><span className="text-slate-400">Documents : </span>{detail.nb_extraits}/{detail.nb_documents}</div>
                <div><span className="text-slate-400">Pièces : </span><span className="text-emerald-600">{detail.nb_pieces_presentes}</span> / <span className="text-amber-600">{detail.nb_pieces_manquantes}</span></div>
              </div>
              {detail.resume && <p className="bg-slate-50 border border-slate-200 rounded p-3">{detail.resume}</p>}
              {detail.extraction?.identite && (
                <div>
                  <h5 className="font-semibold text-violet-700 mb-1">Identité</h5>
                  {Object.entries(detail.extraction.identite).map(([k, v]) => (
                    <div key={k} className="flex justify-between border-b border-slate-100 py-0.5"><span className="text-slate-500">{k}</span><span className="font-medium text-right">{jval(v)}</span></div>
                  ))}
                </div>
              )}
              {detail.extraction?.operation && (
                <div>
                  <h5 className="font-semibold text-violet-700 mb-1">Opération</h5>
                  {Object.entries(detail.extraction.operation).map(([k, v]) => (
                    <div key={k} className="flex justify-between border-b border-slate-100 py-0.5"><span className="text-slate-500">{k}</span><span className="font-medium text-right">{jval(v)}</span></div>
                  ))}
                </div>
              )}
              {Array.isArray(detail.documents) && detail.documents.length > 0 && (
                <div>
                  <h5 className="font-semibold text-violet-700 mb-1">Documents analysés</h5>
                  <ul className="list-disc pl-5 text-slate-600">
                    {detail.documents.map((d, i) => <li key={i}>{d.filename} {d.extracted ? '✓' : '✗'}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
